import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  FormHelperText,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, VisibilityOff, LockReset, Refresh } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  assigned_instances: string[];
  // Live Alpaca credentials
  alpaca_live_account_number?: string;
  alpaca_live_api_key?: string;
  alpaca_live_api_secret?: string;
  // Paper Alpaca credentials
  alpaca_paper_account_number?: string;
  alpaca_paper_api_key?: string;
  alpaca_paper_api_secret?: string;
  created_at: string;
  last_login?: string;
}

interface LoginLogEntry {
  id: number;
  user_id: number;
  username: string;
  action: string;          // 'login' | 'login_failed' | 'logout'
  details?: string;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;       // ISO UTC
}

interface LoginSummary {
  successful_logins: number;
  failed_logins: number;
  unique_ips: number;
  unique_users: number;
  days: number;
}

// Parse a raw user-agent string into a friendly "Browser · OS" label
const parseUserAgent = (ua?: string): string => {
  if (!ua) return '—';
  let browser = 'Unknown browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  else if (/curl|python|wget|httpie/i.test(ua)) browser = 'Script/CLI';

  let os = '';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return os ? `${browser} · ${os}` : browser;
};

// Format a UTC timestamp in Eastern Time, matching the rest of the app
const formatET = (timestamp?: string | null): string => {
  if (!timestamp) return 'Never';
  // SQLite timestamps come without timezone info - they are UTC
  const iso = /Z$|[+-]\d{2}:\d{2}$/.test(timestamp) ? timestamp : timestamp.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return timestamp;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }) + ' ET';
};

// Must match backend rules in src/models/auth_models.py
const validatePassword = (pw: string): string | null => {
  if (pw.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter';
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(pw)) return 'Password must contain at least one symbol (e.g. ! @ # $ %)';
  return null;
};

const AVAILABLE_INSTANCES = ['V1', 'E_OPTIONS', 'OPTIONS_PCS', 'TOPORB', 'TIER3', 'LIVE', 'GROK', 'FUTURES'];

// Display name mapping for instances
const INSTANCE_DISPLAY_NAMES: { [key: string]: string } = {
  'V1': 'OPTIONS',
  'E_OPTIONS': 'E-OPTIONS',
  'OPTIONS_PCS': 'OPTIONS PCS',
  'TOPORB': 'TIER2',
  'LIVE': 'LIVE',
  'GROK': 'GROK',
  'TIER3': 'TIER3',
  'FUTURES': 'Futures'
};

const UserManagement: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create/Edit User Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    status: 'active',
    assigned_instances: [] as string[],
    // Live Alpaca credentials
    alpaca_live_account_number: '',
    alpaca_live_api_key: '',
    alpaca_live_api_secret: '',
    // Paper Alpaca credentials
    alpaca_paper_account_number: '',
    alpaca_paper_api_key: '',
    alpaca_paper_api_secret: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [passwordError, setPasswordError] = useState('');

  // Reset Password Dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');

  // Tabs: 0 = Users, 1 = Login Report
  const [activeTab, setActiveTab] = useState(0);

  // Login Report state
  const [loginLogs, setLoginLogs] = useState<LoginLogEntry[]>([]);
  const [loginSummary, setLoginSummary] = useState<LoginSummary | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [filterUsername, setFilterUsername] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterDays, setFilterDays] = useState(30);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchLoginHistory = async () => {
    setLoginLoading(true);
    try {
      const params = new URLSearchParams({ days: String(filterDays), limit: '500' });
      if (filterUsername !== 'all') params.set('username', filterUsername);
      if (filterEvent !== 'all') params.set('event', filterEvent);

      const response = await fetch(`/api/auth/login-history?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch login history');
      }

      const data = await response.json();
      setLoginLogs(data.logs || []);
      setLoginSummary(data.summary || null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch login history');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchLoginHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterUsername, filterEvent, filterDays]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      // Handle different error types
      if (err.message) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else if (err.detail) {
        setError(err.detail);
      } else {
        setError('Failed to fetch users. Please try again.');
        console.error('Error fetching users:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        password: '',
        role: user.role,
        status: user.status,
        assigned_instances: user.assigned_instances || [],
        // Live Alpaca credentials
        alpaca_live_account_number: user.alpaca_live_account_number || '',
        alpaca_live_api_key: user.alpaca_live_api_key || '',
        alpaca_live_api_secret: user.alpaca_live_api_secret || '',
        // Paper Alpaca credentials
        alpaca_paper_account_number: user.alpaca_paper_account_number || '',
        alpaca_paper_api_key: user.alpaca_paper_api_key || '',
        alpaca_paper_api_secret: user.alpaca_paper_api_secret || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'user',
        status: 'active',
        assigned_instances: [],
        // Live Alpaca credentials
        alpaca_live_account_number: '',
        alpaca_live_api_key: '',
        alpaca_live_api_secret: '',
        // Paper Alpaca credentials
        alpaca_paper_account_number: '',
        alpaca_paper_api_key: '',
        alpaca_paper_api_secret: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setError('');
    setPasswordError('');
  };

  const handleSaveUser = async () => {
    setError('');
    setSuccess('');
    setPasswordError('');

    // Validate password before submitting (create only - matches backend rules)
    if (!editingUser) {
      const pwError = validatePassword(formData.password);
      if (pwError) {
        setPasswordError(pwError);
        setError(pwError);
        return;
      }
    }

    try {
      const url = editingUser
        ? `/api/auth/users/${editingUser.id}`
        : '/api/auth/users';
      const method = editingUser ? 'PUT' : 'POST';

      const payload: any = {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        status: formData.status,
        assigned_instances: formData.assigned_instances,
        // Live Alpaca credentials
        alpaca_live_account_number: formData.alpaca_live_account_number || null,
        alpaca_live_api_key: formData.alpaca_live_api_key || null,
        alpaca_live_api_secret: formData.alpaca_live_api_secret || null,
        // Paper Alpaca credentials
        alpaca_paper_account_number: formData.alpaca_paper_account_number || null,
        alpaca_paper_api_key: formData.alpaca_paper_api_key || null,
        alpaca_paper_api_secret: formData.alpaca_paper_api_secret || null,
      };

      if (!editingUser) {
        payload.username = formData.username;
        payload.password = formData.password;
      }

      console.log('Sending user creation request:', { url, method, payload });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorMessage = 'Failed to save user';
        try {
          const data = await response.json();
          console.log('Error response data:', data);
          
          // Handle FastAPI validation errors (422)
          if (Array.isArray(data.detail)) {
            // Extract validation error messages
            const validationErrors = data.detail.map((err: any) => {
              const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
              return `${field}: ${err.msg}`;
            }).join(', ');
            errorMessage = validationErrors;
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (data.detail) {
            errorMessage = JSON.stringify(data.detail);
          } else {
            errorMessage = JSON.stringify(data);
          }
        } catch (jsonError) {
          console.error('Failed to parse error response:', jsonError);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Success response:', result);
      
      setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
      handleCloseDialog();
      fetchUsers();
    } catch (err: any) {
      // Handle different error types
      console.error('Full error object:', err);
      
      if (err.message) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else if (err.detail) {
        setError(err.detail);
      } else {
        setError('An unexpected error occurred. Please check the browser console for details.');
        console.error('Error creating/updating user:', err);
      }
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }

      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err: any) {
      // Handle different error types
      if (err.message) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else if (err.detail) {
        setError(err.detail);
      } else {
        setError('Failed to delete user. Please try again.');
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleResetPassword = async () => {
    if (!resetUserId) return;

    setResetPasswordError('');
    const pwError = validatePassword(newPassword);
    if (pwError) {
      setResetPasswordError(pwError);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: resetUserId,
          new_password: newPassword,
          force_change: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess('Password reset successfully. User will be required to change it on next login.');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
      setResetUserId(null);
      setResetPasswordError('');
    } catch (err: any) {
      // Show the error inside the dialog (a page-level alert would be hidden behind it)
      if (err.message) {
        setResetPasswordError(err.message);
      } else if (typeof err === 'string') {
        setResetPasswordError(err);
      } else if (err.detail) {
        setResetPasswordError(err.detail);
      } else {
        setResetPasswordError('Failed to reset password. Please try again.');
        console.error('Error resetting password:', err);
      }
    }
  };

  const toggleInstance = (instance: string) => {
    setFormData(prev => ({
      ...prev,
      assigned_instances: prev.assigned_instances.includes(instance)
        ? prev.assigned_instances.filter(i => i !== instance)
        : [...prev.assigned_instances, instance],
    }));
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">User Management</Typography>
        {activeTab === 0 ? (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Create User
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchLoginHistory}
            disabled={loginLoading}
          >
            Refresh
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Users" />
        <Tab label="Login Report" />
      </Tabs>

      {error && !dialogOpen && !resetPasswordDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {activeTab === 0 && (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Instances</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={user.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    color={user.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.role === 'admin' ? (
                    <Typography variant="body2" color="text.secondary">All</Typography>
                  ) : (
                    user.assigned_instances?.map(inst => (
                      <Chip key={inst} label={INSTANCE_DISPLAY_NAMES[inst] || inst} size="small" sx={{ mr: 0.5 }} />
                    ))
                  )}
                </TableCell>
                <TableCell>
                  {formatET(user.last_login)}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(user)}
                    title="Edit User"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setResetUserId(user.id);
                      setResetPasswordDialogOpen(true);
                    }}
                    title="Reset Password"
                  >
                    <LockReset fontSize="small" />
                  </IconButton>
                  {user.username !== 'admin' && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Delete User"
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={filterUsername}
                label="User"
                onChange={(e) => setFilterUsername(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.username}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Event</InputLabel>
              <Select
                value={filterEvent}
                label="Event"
                onChange={(e) => setFilterEvent(e.target.value)}
              >
                <MenuItem value="all">All Events</MenuItem>
                <MenuItem value="login">Successful Login</MenuItem>
                <MenuItem value="login_failed">Failed Login</MenuItem>
                <MenuItem value="logout">Logout</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={filterDays}
                label="Period"
                onChange={(e) => setFilterDays(Number(e.target.value))}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={365}>Last year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Summary cards */}
          {loginSummary && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Card sx={{ minWidth: 170, flex: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">Successful Logins</Typography>
                  <Typography variant="h5" sx={{ color: '#4caf50' }}>{loginSummary.successful_logins}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 170, flex: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">Failed Logins</Typography>
                  <Typography variant="h5" sx={{ color: loginSummary.failed_logins > 0 ? '#f44336' : 'inherit' }}>
                    {loginSummary.failed_logins}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 170, flex: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">Unique IP Addresses</Typography>
                  <Typography variant="h5">{loginSummary.unique_ips}</Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 170, flex: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">Users Seen</Typography>
                  <Typography variant="h5">{loginSummary.unique_users}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Login history table */}
          {loginLoading && loginLogs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time (ET)</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No login activity found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    loginLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatET(log.timestamp)}</TableCell>
                        <TableCell>{log.username}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              log.action === 'login' ? 'Success'
                                : log.action === 'login_failed' ? 'Failed'
                                : 'Logout'
                            }
                            color={
                              log.action === 'login' ? 'success'
                                : log.action === 'login_failed' ? 'error'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{log.ip_address || '—'}</TableCell>
                        <TableCell>
                          <Tooltip title={log.user_agent || 'No user agent recorded'} arrow>
                            <span>{parseUserAgent(log.user_agent)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {log.details || ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              margin="normal"
              disabled={!!editingUser}
              required
            />

            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
            />

            {!editingUser && (
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (passwordError) setPasswordError('');
                }}
                margin="normal"
                required
                error={!!passwordError}
                helperText={passwordError || 'Min 8 characters, 1 uppercase letter, 1 symbol (e.g. ! @ # $ %)'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            {formData.role !== 'admin' && (
              <FormControl fullWidth margin="normal">
                <Typography variant="subtitle2" gutterBottom>
                  Assigned Instances
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {AVAILABLE_INSTANCES.map(instance => (
                    <FormControlLabel
                      key={instance}
                      control={
                        <Checkbox
                          checked={formData.assigned_instances.includes(instance)}
                          onChange={() => toggleInstance(instance)}
                        />
                      }
                      label={INSTANCE_DISPLAY_NAMES[instance] || instance}
                    />
                  ))}
                </Box>
                <FormHelperText>Select which instances this user can access</FormHelperText>
              </FormControl>
            )}

            {/* Live Alpaca Credentials */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#4caf50' }}>
              Live Alpaca Credentials (Production)
            </Typography>
            
            <TextField
              fullWidth
              label="Live Account Number"
              value={formData.alpaca_live_account_number}
              onChange={(e) => setFormData({ ...formData, alpaca_live_account_number: e.target.value })}
              margin="normal"
              helperText="For future features - Production trading"
            />

            <TextField
              fullWidth
              label="Live API Key"
              value={formData.alpaca_live_api_key}
              onChange={(e) => setFormData({ ...formData, alpaca_live_api_key: e.target.value })}
              margin="normal"
              helperText="For future features - Production trading"
            />

            <TextField
              fullWidth
              label="Live API Secret"
              type="password"
              value={formData.alpaca_live_api_secret}
              onChange={(e) => setFormData({ ...formData, alpaca_live_api_secret: e.target.value })}
              margin="normal"
              helperText="For future features - Production trading"
            />

            {/* Paper Alpaca Credentials */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#2196f3' }}>
              Paper Alpaca Credentials (Testing)
            </Typography>
            
            <TextField
              fullWidth
              label="Paper Account Number"
              value={formData.alpaca_paper_account_number}
              onChange={(e) => setFormData({ ...formData, alpaca_paper_account_number: e.target.value })}
              margin="normal"
              helperText="For future features - Testing/simulation"
            />

            <TextField
              fullWidth
              label="Paper API Key"
              value={formData.alpaca_paper_api_key}
              onChange={(e) => setFormData({ ...formData, alpaca_paper_api_key: e.target.value })}
              margin="normal"
              helperText="For future features - Testing/simulation"
            />

            <TextField
              fullWidth
              label="Paper API Secret"
              type="password"
              value={formData.alpaca_paper_api_secret}
              onChange={(e) => setFormData({ ...formData, alpaca_paper_api_secret: e.target.value })}
              margin="normal"
              helperText="For future features - Testing/simulation"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => { setResetPasswordDialogOpen(false); setResetPasswordError(''); }}>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          {resetPasswordError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setResetPasswordError('')}>
              {resetPasswordError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (resetPasswordError) setResetPasswordError('');
            }}
            margin="normal"
            error={!!resetPasswordError}
            helperText={resetPasswordError || 'Min 8 characters, 1 uppercase letter, 1 symbol (e.g. ! @ # $ %)'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            User will be required to change this password on next login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetPasswordDialogOpen(false); setResetPasswordError(''); }}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="primary">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;

